import { useMutation } from '@tanstack/react-query-v4';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  SourceDetails,
  FraudCategory,
  AuditType,
} from '@scale/shared/trustAndSafety/tnsAudits/types';

export interface CreateSuspiciousBehaviorAuditResponse {
  createdAuditIds: string[];
  matchedAuditIds: string[];
  failedRequestIndices: number[];
  createdOutlierTasks: boolean;
}

type BaseSuspiciousBehaviorAudit = {
  contributorId: string;
  reason: string;
  files: File[];
  sourceDetails?: SourceDetails;
};

type FraudSuspiciousBehaviorAudit = BaseSuspiciousBehaviorAudit & {
  type: AuditType.ManuallyReportedFraud;
  fraudCategory: FraudCategory;
};

type CheatingOrSpamSuspiciousBehaviorAudit = BaseSuspiciousBehaviorAudit & {
  type: AuditType.TaskingCheating | AuditType.TaskingSpam;
  taskAttemptIds: string[];
};

export type CreateSuspiciousBehaviorAuditRequest =
  | FraudSuspiciousBehaviorAudit
  | CheatingOrSpamSuspiciousBehaviorAudit;

export const useCreateSuspiciousBehaviorAudit = () => {
  return useMutation({
    mutationFn: async (payload: CreateSuspiciousBehaviorAuditRequest) => {
      const formData = new FormData();
      formData.append('contributorId', payload.contributorId);
      if (payload.type === AuditType.ManuallyReportedFraud) {
        formData.append('fraudCategory', payload.fraudCategory);
      }
      if (payload.type === AuditType.TaskingCheating || payload.type === AuditType.TaskingSpam) {
        payload.taskAttemptIds.forEach(id => formData.append('taskAttemptIds[]', id));
      }
      formData.append('reason', payload.reason);
      formData.append('type', payload.type);
      if (payload.sourceDetails) {
        formData.append('sourceDetails', JSON.stringify(payload.sourceDetails));
      }
      payload.files.forEach(file => {
        formData.append('files', file);
      });

      return await apiFetch<CreateSuspiciousBehaviorAuditResponse>(
        null,
        'internal/tns-audits/create_suspicious_behavior_audit',
        { method: 'POST', body: formData },
      );
    },
    onSuccess: async () => {
      // TODO <cathy-scale>: invalidate queries once audits are surfaced in the UI
    },
  });
};
